import { DeviceTypeConfigurations, type KnownDeviceType } from "verdi-common";
import type { DataVisibility, DevicePortConfig } from "verdi-common/Configurations/portConfigs";
import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import { DEVICE_COLLECTION_NAME } from "verdi-common/schemas/device/device";
import { THIRD_PARTY_DEVICE_COLLECTION_NAME } from "verdi-common/schemas/thirdPartyDevice/thirdPartyDevice";
import { MasterIndex } from "verdi-web-client";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";

const LEVELS = ["shown", "diagnostic", "adminOnly"] as const satisfies readonly ("shown" | DataVisibility)[];

/** Least to most hidden. */
export type StreamVisibility = (typeof LEVELS)[number];

type HostReference = NonNullable<ListDataStreams200Item["latestFragment"]>["device"];

export function visibilityRank(visibility: StreamVisibility): number {
    return LEVELS.indexOf(visibility);
}

function stricter(a: StreamVisibility, b: StreamVisibility): StreamVisibility {
    return visibilityRank(a) >= visibilityRank(b) ? a : b;
}

function deviceType({ id, collection }: HostReference): string | undefined {
    if (collection === DEVICE_COLLECTION_NAME) {
        return (MasterIndex.irrigationDevice.byID[id] ?? MasterIndex.blockValve.byID[id])?.type;
    }
    if (collection === THIRD_PARTY_DEVICE_COLLECTION_NAME) return MasterIndex.thirdPartyDevice.byID[id]?.deviceType;
    return undefined;
}

function portVisibility(stream: ListDataStreams200Item): StreamVisibility {
    const fragment = stream.latestFragment;
    if (!fragment) return "shown";
    const type = deviceType(fragment.device);
    const ports = type ? DeviceTypeConfigurations[type as KnownDeviceType]?.sensorPortConfiguration : undefined;
    const port = ports?.[fragment.portId as keyof DevicePortConfig];
    if (!port?.isFixed) return "shown";
    return stricter(port.visibility ?? "shown", port.isInternal ? "diagnostic" : "shown");
}

export function streamVisibility(stream: ListDataStreams200Item): StreamVisibility {
    const categoryVisibility = getUnitCategoryConfiguration(stream.unitCategory)?.visibility ?? "shown";
    return stricter(portVisibility(stream), categoryVisibility);
}

/** The most hidden level a viewer gets, from the card's option and whether an admin is looking. */
export function visibleUpTo({
    hideDiagnostics,
    isAdmin,
}: {
    hideDiagnostics: boolean;
    isAdmin: boolean;
}): StreamVisibility {
    if (hideDiagnostics) return "shown";
    return isAdmin ? "adminOnly" : "diagnostic";
}

export function isVisibleUpTo(stream: ListDataStreams200Item, upTo: StreamVisibility): boolean {
    return visibilityRank(streamVisibility(stream)) <= visibilityRank(upTo);
}
